import React from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import DialpadOutlinedIcon from '@mui/icons-material/DialpadOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { Controller, type Control, type UseFormSetValue } from 'react-hook-form';
import AdminFormRow from '../../../../components/admin/AdminFormRow';
import { formatPhoneNumber } from '../../../../utils/formatUtils';
import type {
    StoreDetailsInviteUserFormValues,
    StoreDetailsInviteUserLoginMethod,
} from '../../../../types/features/storeDetails/users';
import { adminColors, adminRadii, adminSpacing, adminTypography } from '../../../../styles/adminDesignTokens';
import {
    adminButtonSx,
    adminButtonToneSx,
    adminCheckboxSx,
    adminCombineSx,
    adminDialogActionsSx,
    adminDialogContentSx,
    adminDialogSx,
    adminDialogTitleSx,
    adminFormControlSx,
    adminFormFieldSx,
} from '../../../../styles/adminComponentStyles';
import { useStoreDetailsAddUserDialogLogic } from '../hooks/useStoreDetailsAddUserDialogLogic';
import {
    formatStoreDetailsInviteUserRole,
    STORE_DETAILS_INVITE_USER_ROLES,
} from '../utils/storeDetailsInviteUserUtils';

type StoreDetailsAddUserDialogProps = {
    open: boolean;
    control: Control<StoreDetailsInviteUserFormValues>;
    setValue: UseFormSetValue<StoreDetailsInviteUserFormValues>;
    submitting: boolean;
    inviteIsValid: boolean;
    onClose: () => void;
    onSubmit: () => void;
};

const loginMethodOptions: Array<{
    description: string;
    key: StoreDetailsInviteUserLoginMethod;
    label: string;
}> = [
    { key: 'phone', label: 'Phone', description: 'Send OTP to a phone number.' },
    { key: 'email', label: 'Email', description: 'Send OTP to an email address.' },
    { key: 'pin', label: 'Pin', description: 'Operator cashier login only.' },
];

const loginMethodGridSx = {
    display: 'grid',
    gap: adminSpacing.sm,
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
} as const;

const loginMethodButtonSx = {
    alignItems: 'flex-start',
    borderRadius: adminRadii.md,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    minHeight: 78,
    p: adminSpacing.md,
    textAlign: 'left',
    textTransform: 'none',
    '& .MuiSvgIcon-root': {
        fontSize: 18,
    },
} as const;

const StoreDetailsAddUserDialog: React.FC<StoreDetailsAddUserDialogProps> = ({
    open,
    control,
    setValue,
    submitting,
    inviteIsValid,
    onClose,
    onSubmit,
}) => {
    const {
        handleFormSubmit,
        handleLoginMethodChange,
        handlePhoneChange,
        handlePinChange,
        handleRoleChange,
        pinLoginAllowed,
        selectedLoginMethod,
        showRecoPermission,
    } = useStoreDetailsAddUserDialogLogic({ control, setValue, onSubmit });

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: adminCombineSx(adminDialogSx, { maxWidth: '570px' }),
            }}
        >
            <DialogTitle
                sx={adminCombineSx(adminDialogTitleSx, {
                    backgroundColor: 'rgb(239, 246, 255)',
                    color: adminColors.primary,
                    fontWeight: 600,
                    marginBottom: 0,
                })}
            >
                Add User
            </DialogTitle>
            <DialogContent sx={adminCombineSx(adminDialogContentSx, { p: '20px', pt: '20px !important' })}>
                <Box component="form" id="invite-user-form" onSubmit={handleFormSubmit}>
                    <Stack spacing={2}>
                        <AdminFormRow label="Role" required>
                            <FormControl fullWidth sx={adminFormControlSx}>
                                <Controller
                                    name="role"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            value={field.value}
                                            onChange={(event) => {
                                                handleRoleChange(event.target.value as StoreDetailsInviteUserFormValues['role']);
                                            }}
                                        >
                                            {STORE_DETAILS_INVITE_USER_ROLES.map((role) => (
                                                <MenuItem key={role} value={role}>
                                                    {formatStoreDetailsInviteUserRole(role)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                            </FormControl>
                        </AdminFormRow>

                        <AdminFormRow label="Name" required>
                            <Controller
                                name="name"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        sx={adminFormFieldSx}
                                    />
                                )}
                            />
                        </AdminFormRow>

                        {showRecoPermission ? (
                            <AdminFormRow label="Permissions">
                                <Box
                                    sx={{
                                        backgroundColor: adminColors.infoBackground,
                                        border: `1px solid ${adminColors.primarySoft}`,
                                        borderRadius: adminRadii.md,
                                        px: adminSpacing.md,
                                        py: 1.25,
                                    }}
                                >
                                    <Controller
                                        name="can_approve_reconciliation"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControlLabel
                                                control={(
                                                    <Checkbox
                                                        checked={field.value}
                                                        onChange={(event) => field.onChange(event.target.checked)}
                                                        sx={adminCheckboxSx}
                                                    />
                                                )}
                                                label={(
                                                    <Box>
                                                        <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
                                                            Can reconcile and approve mismatched audits
                                                        </Typography>
                                                        <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>
                                                            Adds the Reco Officer permission and allows approval actions.
                                                        </Typography>
                                                    </Box>
                                                )}
                                                sx={{
                                                    alignItems: 'flex-start',
                                                    m: 0,
                                                    '& .MuiFormControlLabel-label': {
                                                        pl: adminSpacing.sm,
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </AdminFormRow>
                        ) : null}

                        <Controller
                            name="loginMethod"
                            control={control}
                            render={({ fieldState }) => (
                                <AdminFormRow label="Login Type" required>
                                    <Box role="group" aria-label="Login type" sx={loginMethodGridSx}>
                                        {loginMethodOptions.map((option) => {
                                            const selected = selectedLoginMethod === option.key;
                                            const disabled = option.key === 'pin' && !pinLoginAllowed;
                                            return (
                                                <Button
                                                    key={option.key}
                                                    type="button"
                                                    variant="outlined"
                                                    disabled={disabled}
                                                    aria-pressed={selected}
                                                    onClick={() => handleLoginMethodChange(option.key)}
                                                    sx={adminCombineSx(
                                                        loginMethodButtonSx,
                                                        {
                                                            backgroundColor: selected ? adminColors.primarySoft : adminColors.surface,
                                                            borderColor: selected ? adminColors.primary : adminColors.border,
                                                            color: disabled ? adminColors.disabledText : adminColors.text,
                                                            boxShadow: selected ? `inset 3px 0 0 ${adminColors.primary}` : 'none',
                                                            '&:hover': {
                                                                backgroundColor: selected ? adminColors.primarySoft : adminColors.surfaceAlt,
                                                                borderColor: selected ? adminColors.primary : adminColors.borderHover,
                                                            },
                                                        },
                                                    )}
                                                >
                                                    <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75, mb: 0.5 }}>
                                                        {option.key === 'phone' ? <PhoneOutlinedIcon /> : null}
                                                        {option.key === 'email' ? <EmailOutlinedIcon /> : null}
                                                        {option.key === 'pin' ? <DialpadOutlinedIcon /> : null}
                                                        <Typography component="span" sx={{ ...adminTypography.bodyStrong, color: 'inherit' }}>
                                                            {option.label}
                                                        </Typography>
                                                    </Box>
                                                    <Typography component="span" sx={{ ...adminTypography.helper, color: disabled ? adminColors.disabledText : adminColors.mutedText }}>
                                                        {option.description}
                                                    </Typography>
                                                </Button>
                                            );
                                        })}
                                    </Box>
                                    <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText, mt: 0.75 }}>
                                        {selectedLoginMethod === 'phone'
                                            ? 'Phone login requires a phone number.'
                                            : selectedLoginMethod === 'email'
                                                ? 'Email login requires an email address.'
                                                : 'PIN login is only available for operator users.'}
                                    </Typography>
                                    {fieldState.error ? <FormHelperText error>{fieldState.error.message}</FormHelperText> : null}
                                </AdminFormRow>
                            )}
                        />

                        {selectedLoginMethod === 'phone' ? (
                            <AdminFormRow label="Phone" required>
                                <Controller
                                    name="phone_number"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            fullWidth
                                            placeholder="2095550134"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message || 'Required for phone login.'}
                                            value={field.value ? formatPhoneNumber(field.value) : ''}
                                            onChange={(event) => handlePhoneChange(event.target.value)}
                                            inputProps={{ inputMode: 'numeric' }}
                                            sx={adminFormFieldSx}
                                        />
                                    )}
                                />
                            </AdminFormRow>
                        ) : null}

                        {selectedLoginMethod === 'phone' ? (
                            <AdminFormRow label="Email Address">
                                <Controller
                                    name="email"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            placeholder="Optional contact email"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message || 'Optional contact.'}
                                            sx={adminFormFieldSx}
                                        />
                                    )}
                                />
                            </AdminFormRow>
                        ) : null}

                        {selectedLoginMethod === 'email' ? (
                            <AdminFormRow label="Email Address" required>
                                <Controller
                                    name="email"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            placeholder="user@example.com"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message || 'Required for email login.'}
                                            sx={adminFormFieldSx}
                                        />
                                    )}
                                />
                            </AdminFormRow>
                        ) : null}

                        {selectedLoginMethod === 'email' ? (
                            <AdminFormRow label="Phone">
                                <Controller
                                    name="phone_number"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            fullWidth
                                            placeholder="Optional contact phone"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message || 'Optional contact.'}
                                            value={field.value ? formatPhoneNumber(field.value) : ''}
                                            onChange={(event) => handlePhoneChange(event.target.value)}
                                            inputProps={{ inputMode: 'numeric' }}
                                            sx={adminFormFieldSx}
                                        />
                                    )}
                                />
                            </AdminFormRow>
                        ) : null}

                        {selectedLoginMethod === 'pin' ? (
                            <AdminFormRow label="Cashier PIN" required>
                                <Controller
                                    name="cashier_mode_pin"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <TextField
                                            fullWidth
                                            type="password"
                                            placeholder="Six digits"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message || 'Six digits. Allowed only for operator users.'}
                                            value={field.value}
                                            onChange={(event) => handlePinChange(event.target.value)}
                                            inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                                            sx={adminFormFieldSx}
                                        />
                                    )}
                                />
                            </AdminFormRow>
                        ) : null}
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions sx={adminDialogActionsSx}>
                <Button
                    onClick={onClose}
                    disabled={submitting}
                    variant="outlined"
                    sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary, { height: 40 })}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    form="invite-user-form"
                    variant="contained"
                    disabled={!inviteIsValid || submitting}
                    sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary, { height: 40 })}
                >
                    {submitting ? 'Sending...' : 'Send Invite'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default React.memo(StoreDetailsAddUserDialog);
